import { CSSProperties } from 'react';
import {
  DragBounds,
  Points,
  WellDetailsStates,
  ZoomSyncContext,
} from 'business/models/CustomModels';
import { IImageZoomDTO, IWellImageParams, RegionsDTO } from 'business/models/Models';
import WellService from 'business/services/WellService';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import { ImageSizeDetails } from './constants';

export const exportPoiCsv = async (
  expId: number,
  plateId: number,
  wellNum: number,
  dropNumber: number,
) => {
  const wellService = new WellService();
  const fileResponse = await wellService.generatePoiCsvFile(
    expId,
    wellNum ? true : false,
    plateId,
    wellNum,
    dropNumber,
  );
  const blob = new Blob([fileResponse.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileResponse.fileName;
  link.click();
};

export const changeDrawingIcon = (e, icon: string) => {
  const stage = e.target.getStage();
  if (!stage) {
    return;
  }
  const container = stage.container();
  if (container.style.cursor === icon) {
    return;
  }

  container.style.cursor = icon;
};

export const getMinimumZoomLevel = (
  isIndependentZoom: boolean,
  totalWells: number,
  minZoomLevel: number,
) => {
  if (isIndependentZoom || totalWells === 1) {
    return 1;
  }
  return minZoomLevel;
};

//key: plateId-wellNum-dropNumber-regionName
const regionIdCache: Record<string, number> = {};

/**
 * Build the full region ID cache from all regions.
 */
export function buildRegionIdLookupCache(allRegions: RegionsDTO[]): void {
  if (!allRegions || allRegions.length === 0) {
    return;
  }

  for (const region of allRegions) {
    if (region.drops?.length) {
      for (const drop of region.drops) {
        if (!drop) {
          continue; // Skip if drop is undefined or null
        }
        const key = `${drop.plateId}-${drop.wellNumber}-${drop.dropNumber}-${region.name}`;
        regionIdCache[key] = drop.regionId;
      }
    }
  }
}

/**
 * Look up a region ID from the cache; if not found, fall back to search logic.
 */
export function getRegionIdCached(
  plateID: number,
  wellNum: number,
  dropNumber: number,
  regionName: string,
  allRegions?: RegionsDTO[],
): number | undefined {
  const key = `${plateID}-${wellNum}-${dropNumber}-${regionName}`;
  // Try cache first
  if (key in regionIdCache) {
    return regionIdCache[key];
  }

  if (!allRegions || allRegions.length === 0) {
    return null;
  }
  // Fallback to inline getRegionId logic --
  // if for some reason cached data is not available(highly unlikely)
  const selectedRegion = allRegions.find((r) => r.name === regionName);
  const regionDetail = selectedRegion?.drops?.find(
    (d) =>
      d.plateId === plateID && d.wellNumber === wellNum && d.dropNumber === dropNumber,
  );

  regionIdCache[key] = regionDetail?.regionId;
  return regionDetail?.regionId;
}

export const getWellImageParams = (WellDetails: WellDetailsStates): IWellImageParams => {
  return {
    pid: WellDetails.plateID,
    wellNumber: WellDetails.wellNum,
    batchId: WellDetails.imageBatchId,
    dropNumber: WellDetails.dropNumber,
    cpID: WellDetails.cpID,
    regionID: WellDetails.regionID,
  };
};

export const DEFAULT_OVERLAY_OPACITY = 50;

export const getImageZoomVisibleArea = (
  stage,
  tpObject: TransformPoints,
  scale,
): IImageZoomDTO => {
  const position = stage.position();
  const stageWidth = stage.width();
  const stageHeight = stage.height();
  if (!tpObject) {
    return {} as IImageZoomDTO;
  }
  const { imageHeight, imageWidth, scalingWidth, scalingHeight, leftMargin, topMargin } =
    tpObject;

  const scaleRatio = imageWidth / scalingWidth;

  const visibleArea = {
    x: Math.max(0, ((-position.x - leftMargin * scale) / scale) * scaleRatio),
    y: Math.max(0, ((-position.y - topMargin * scale) / scale) * scaleRatio),
    width: Math.min(imageWidth, (stageWidth / scale) * scaleRatio),
    height: Math.min(imageHeight, (stageHeight / scale) * scaleRatio),
  };

  return {
    scale,
    ...visibleArea,
    outputWidth: Math.round(Math.min(stageWidth, scalingWidth * scale)),
    outputHeight: Math.round(Math.min(stageHeight, scalingHeight * scale)),
  } as IImageZoomDTO;
};

export const getUnlockedOverlayFollowerPosition = (
  followerPos: Points,
  newFollowerScale: number,
  syncContext: ZoomSyncContext,
): Points => {
  const { fromPos, toPos, fromScale } = syncContext;
  const offset: Points = {
    x: (followerPos.x - fromPos.x) / fromScale,
    y: (followerPos.y - fromPos.y) / fromScale,
  };
  return {
    x: toPos.x + offset.x * newFollowerScale,
    y: toPos.y + offset.y * newFollowerScale,
  };
};

export const getPannedFollowerPosition = (
  followerStart: Points,
  driverStart: Points,
  driverPosition: Points,
): Points => ({
  x: followerStart.x + driverPosition.x - driverStart.x,
  y: followerStart.y + driverPosition.y - driverStart.y,
});

export const getUnlockedOverlayBasePosition = (
  basePos: Points,
  newScale: number,
  syncContext: ZoomSyncContext,
): Points => {
  if (newScale <= 1) {
    return { x: 0, y: 0 };
  }
  return getUnlockedOverlayFollowerPosition(basePos, newScale, syncContext);
};

export const getUnlockedOverlayPannedPosition = (
  basePos: Points,
  newScale: number,
  syncContext: ZoomSyncContext,
): Points => {
  const { fromPos, fromScale } = syncContext;
  const offset: Points = {
    x: (fromPos.x - basePos.x) / fromScale,
    y: (fromPos.y - basePos.y) / fromScale,
  };
  const baseNewPos = getUnlockedOverlayBasePosition(basePos, newScale, syncContext);
  return {
    x: baseNewPos.x + offset.x * newScale,
    y: baseNewPos.y + offset.y * newScale,
  };
};

export type OverlayDragBoundsParams = {
  scale: number;
  stageSize: Points;
  ownExtent: Points;
  maxExtent: Points;
  basePosition: Points;
};

export const getOverlayDragBounds = ({
  scale,
  stageSize,
  ownExtent,
  maxExtent,
  basePosition,
}: OverlayDragBoundsParams): DragBounds => {
  const scaledOwn: Points = { x: ownExtent.x * scale, y: ownExtent.y * scale };
  const scaledMax: Points = { x: maxExtent.x * scale, y: maxExtent.y * scale };

  const slackX = Math.max(0, (scaledMax.x - scaledOwn.x) / 2);
  const slackY = Math.max(0, (scaledMax.y - scaledOwn.y) / 2);

  const ownOverflowMinX = Math.min(0, stageSize.x - scaledOwn.x);
  const ownOverflowMinY = Math.min(0, stageSize.y - scaledOwn.y);

  return {
    minX: Math.min(ownOverflowMinX, basePosition.x - slackX),
    maxX: Math.max(0, basePosition.x + slackX),
    minY: Math.min(ownOverflowMinY, basePosition.y - slackY),
    maxY: Math.max(0, basePosition.y + slackY),
  };
};

export type LockedOverlayDragBoundsParams = {
  scale: number;
  stageSize: Points;
  ownPosition: Points;
  largestImagePosition: Points;
};

// Locked pan moves both images as one, so the dragged stage is bounded by the normal pan
// range of the largest image, shifted by the dragged stage's offset from it
export const getLockedOverlayDragBounds = ({
  scale,
  stageSize,
  ownPosition,
  largestImagePosition,
}: LockedOverlayDragBoundsParams): DragBounds => {
  const offset: Points = {
    x: ownPosition.x - largestImagePosition.x,
    y: ownPosition.y - largestImagePosition.y,
  };
  const largestImageMin: Points = {
    x: Math.min(0, stageSize.x - stageSize.x * scale),
    y: Math.min(0, stageSize.y - stageSize.y * scale),
  };

  return {
    minX: largestImageMin.x + offset.x,
    maxX: offset.x,
    minY: largestImageMin.y + offset.y,
    maxY: offset.y,
  };
};

export const getLargestRenderedExtent = (
  overlaidImageSizes: ImageSizeDetails[],
): Points | undefined => {
  const known = overlaidImageSizes.filter(
    (details) => details?.scalingWidth > 0 && details?.scalingHeight > 0,
  );
  if (!known.length) {
    return undefined;
  }
  return {
    x: Math.max(...known.map((details) => details.scalingWidth)),
    y: Math.max(...known.map((details) => details.scalingHeight)),
  };
};

export const getCanvasWrapperStyle = (canvasWidth: number): CSSProperties => ({
  width: canvasWidth,
  position: 'relative',
});
